// frontend/src/components/admin/TaxonomyGuide.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  Button,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchemaIcon from '@mui/icons-material/Schema';
import CategoryIcon from '@mui/icons-material/Category';
import StorageIcon from '@mui/icons-material/Storage';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SyncIcon from '@mui/icons-material/Sync';
import PageContainer from '../common/PageContainer';
import TableViewport from '../common/TableViewport';
import ViewHeader from '../ViewHeader';
import { useHeaderConfig } from '../SidebarAndHeaderBar';
import { brewFocusLabel, skillLevelLabel } from '../../utils/labels';

// Diagram component for visual representation
const FlowDiagram = ({ title, steps }) => (
  <Paper variant="outlined" sx={{ p: 3, my: 2 }}>
    <Typography variant="h6" gutterBottom>
      {title}
    </Typography>
    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <Card variant="outlined" sx={{ minWidth: 200, flex: '1 1 auto' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                {step.icon}
                <Typography variant="subtitle2" fontWeight={600}>
                  {step.title}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {step.description}
              </Typography>
            </CardContent>
          </Card>
          {index < steps.length - 1 && (
            <ArrowForwardIcon color="action" sx={{ flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </Stack>
  </Paper>
);

// Code block component
const CodeBlock = ({ code, language = 'bash' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      my: 1,
      bgcolor: 'grey.50',
      fontFamily: 'monospace',
      fontSize: 12,
      overflow: 'auto',
    }}
  >
    <pre style={{ margin: 0 }}>{code}</pre>
  </Paper>
);

export default function TaxonomyGuide() {
  const { setHeaderConfig } = useHeaderConfig();
  const [activeTab, setActiveTab] = useState(0);

  React.useEffect(() => {
    setHeaderConfig({
      title: 'Product Taxonomy System Guide',
      showSearch: false,
      actions: [],
    });
  }, [setHeaderConfig]);

  // Overview section
  const renderOverview = () => (
    <Stack spacing={3}>
      <Alert severity="info">
        The Product Taxonomy System is a comprehensive classification framework that automatically categorizes
        coffee equipment into 40+ precise types, enabling intelligent recommendations, dynamic Shopify
        collections, and compatibility matching.
      </Alert>

      <Typography variant="h6">What is Product Taxonomy?</Typography>
      <Typography variant="body2" color="text.secondary">
        Product taxonomy is a structured classification system that organizes products into hierarchical
        categories with associated attributes. Unlike simple categories like "Grinders" or "Accessories", our
        taxonomy system provides granular types like "Single Dose Espresso Grinder" or "Bottomless
        Portafilter" with rich metadata about skill level, brew focus, compatibility, and more.
      </Typography>

      <Typography variant="h6">Why Do We Need It?</Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary="Intelligent Product Recommendations"
            secondary="Automatically suggest compatible accessories (e.g., recommend burr sets that fit a specific grinder)"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Zero-Maintenance Shopify Collections"
            secondary="Collections like 'Espresso Machines' or 'Budget Setup' update automatically as products are added"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Advanced Search & Filtering"
            secondary="Filter by skill level, brew method, price tier, form factor, and 20+ other attributes"
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Compatibility Matching"
            secondary="Ensure burrs match grinder models, portafilters match group head sizes, etc."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Market Segmentation"
            secondary="Target specific customer segments (beginners, pro baristas, travel enthusiasts)"
          />
        </ListItem>
      </List>

      <FlowDiagram
        title="How Taxonomy Flows Through the System"
        steps={[
          {
            icon: <StorageIcon color="primary" />,
            title: 'Product Data',
            description: 'Name, price, tags, vendor from Shopify',
          },
          {
            icon: <CategoryIcon color="primary" />,
            title: 'Inference Engine',
            description: 'AI determines canonical_type and attributes',
          },
          {
            icon: <SchemaIcon color="primary" />,
            title: 'Database Storage',
            description: 'Taxonomy saved in product_taxonomy table',
          },
          {
            icon: <SyncIcon color="primary" />,
            title: 'Shopify Sync',
            description: 'Push as metafields for collections & templates',
          },
          {
            icon: <ShoppingCartIcon color="primary" />,
            title: 'Recommendations',
            description: 'Generate product relations & suggestions',
          },
        ]}
      />
    </Stack>
  );

  // Architecture section
  const renderArchitecture = () => (
    <Stack spacing={3}>
      <Typography variant="h6">System Architecture</Typography>

      <FlowDiagram
        title="Complete Taxonomy Pipeline"
        steps={[
          {
            icon: <Typography fontWeight={600}>1</Typography>,
            title: 'taxonomy_batch.py',
            description: 'Derive taxonomy from product attributes',
          },
          {
            icon: <Typography fontWeight={600}>2</Typography>,
            title: 'sync_taxonomy_metafields.py',
            description: 'Push taxonomy to Shopify as metafields',
          },
          {
            icon: <Typography fontWeight={600}>3</Typography>,
            title: 'create_dynamic_collections.py',
            description: 'Create smart Shopify collections',
          },
          {
            icon: <Typography fontWeight={600}>4</Typography>,
            title: 'relations_batch.py',
            description: 'Generate product recommendations',
          },
        ]}
      />

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Core Files & Modules</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>canonical_taxonomy.py</code>
                  </TableCell>
                  <TableCell>
                    Type definitions, enums, systems, affinity matrix
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>taxonomy.py</code>
                  </TableCell>
                  <TableCell>
                    Inference logic (derive_taxonomy_for_product)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>taxonomy_enhancements.py</code>
                  </TableCell>
                  <TableCell>Enhancement layer adding derived fields</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>taxonomy_batch.py</code>
                  </TableCell>
                  <TableCell>Batch processing script for all products</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>sync_taxonomy_metafields.py</code>
                  </TableCell>
                  <TableCell>Push taxonomy to Shopify metafields</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>sync_product_tags.py</code>
                  </TableCell>
                  <TableCell>Generate namespaced tags from taxonomy</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>models.py</code>
                  </TableCell>
                  <TableCell>Database models (ProductTaxonomy, overrides)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">n8n Taxonomy Agent (AI Content Pipeline)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              The <strong>Custom AI Product Writer</strong> n8n workflow runs separately from the nightly batch.
              It classifies individual products using OpenAI, writing results directly to{' '}
              <code>product_taxonomy_overrides</code>. The next nightly <code>taxonomy_batch.py</code> run
              picks these up and merges them into <code>product_taxonomy</code>.
            </Typography>
            <TableViewport>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell>Fields Written</TableCell>
                    <TableCell>How</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>n8n Taxonomy Agent</TableCell>
                    <TableCell>
                      <code>canonical_type</code>, <code>skill_level</code>, <code>brew_focus</code>,
                      <code>barista_tier</code>, <code>system_memberships</code>,{' '}
                      <code>ideal_for</code>, <code>feature_highlights</code>,{' '}
                      <code>compatible_equipment</code>, <code>maintenance_level</code>,{' '}
                      <code>power_requirement</code>
                    </TableCell>
                    <TableCell>POST /api/admin/taxonomy/overrides per product</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>taxonomy_enhancements.py (auto-derived)</TableCell>
                    <TableCell>
                      <code>equipment_category</code>, <code>grind_range</code>,{' '}
                      <code>skill_level_required</code>, <code>maintenance_level</code>
                    </TableCell>
                    <TableCell>Computed from canonical_type during nightly batch</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Admin UI override</TableCell>
                    <TableCell>
                      Any field in <code>product_taxonomy_overrides</code> (manual corrections)
                    </TableCell>
                    <TableCell>Via Taxonomy Hub → Product Detail → Edit Override</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableViewport>
            <Alert severity="warning">
              Auto-derived fields (<code>equipment_category</code>, <code>grind_range</code>,{' '}
              <code>skill_level_required</code>) are always recomputed from <code>canonical_type</code> during
              the batch — do not set these as manual overrides. The n8n agent no longer writes these fields.
            </Alert>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Database Schema</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" gutterBottom>
            The taxonomy system uses three main tables:
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">
                1. <code>product_taxonomy</code> - Derived taxonomy for each product
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Core fields: canonical_type, skill_level, brew_focus, system_memberships, barista_tier,
                price_band
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Auto-derived fields (from canonical_type via taxonomy_enhancements.py): equipment_category,
                grind_range, maintenance_level, skill_level_required
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                AI-written fields (from n8n Taxonomy Agent → product_taxonomy_overrides): ideal_for,
                feature_highlights, compatible_equipment, power_requirement
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">
                2. <code>product_taxonomy_overrides</code> - Manual product-level overrides
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Any non-null field overrides the inferred value. Keyed by product_id.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">
                3. <code>product_variant_taxonomy_overrides</code> - Variant-specific overrides
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For variants that differ from parent product. Keyed by variant_id.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  // Canonical types section
  const renderCanonicalTypes = () => (
    <Stack spacing={3}>
      <Typography variant="h6">40+ Canonical Product Types</Typography>
      <Alert severity="success">
        Our system recognizes 40+ precise product types, from "Single Dose Espresso Grinder" to "WDT Tool",
        each with specific attributes defining skill level, brew method compatibility, and ideal use cases.
      </Alert>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Grinders (8 types)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Brew Focus</TableCell>
                  <TableCell>Skill Levels</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>single_dose_espresso_grinder</code>
                  </TableCell>
                  <TableCell>Single Dose Espresso Grinder</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('espresso_primary')} color="primary" />
                  </TableCell>
                  <TableCell>{['intermediate', 'advanced', 'pro'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>single_dose_allrounder_grinder</code>
                  </TableCell>
                  <TableCell>Single Dose Allrounder Grinder</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('allrounder')} />
                  </TableCell>
                  <TableCell>{['intermediate', 'advanced', 'pro'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>single_dose_filter_grinder</code>
                  </TableCell>
                  <TableCell>Single Dose Filter Grinder</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('filter_primary')} />
                  </TableCell>
                  <TableCell>{['intermediate', 'advanced'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>hybrid_portable_grinder</code>
                  </TableCell>
                  <TableCell>Hybrid Portable Grinder</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('allrounder')} />
                  </TableCell>
                  <TableCell>{['intermediate', 'advanced'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>hopper_espresso_grinder</code>
                  </TableCell>
                  <TableCell>Hopper Espresso Grinder</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('espresso_primary')} color="primary" />
                  </TableCell>
                  <TableCell>{['beginner', 'intermediate'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>hand_grinder_espresso</code>
                  </TableCell>
                  <TableCell>Hand Grinder (Espresso)</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('espresso_primary')} color="primary" />
                  </TableCell>
                  <TableCell>{['beginner', 'intermediate', 'advanced'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>hand_grinder_filter</code>
                  </TableCell>
                  <TableCell>Hand Grinder (Filter)</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('filter_primary')} />
                  </TableCell>
                  <TableCell>{['beginner', 'intermediate'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>hand_grinder_allrounder</code>
                  </TableCell>
                  <TableCell>Hand Grinder (Allrounder)</TableCell>
                  <TableCell>
                    <Chip size="small" label={brewFocusLabel('allrounder')} />
                  </TableCell>
                  <TableCell>{['beginner', 'intermediate', 'advanced'].map((s) => skillLevelLabel(s)).join(', ')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Espresso Machines (4 types)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Example Products</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>semi_auto_espresso_machine</code>
                  </TableCell>
                  <TableCell>Semi-Automatic Espresso Machine</TableCell>
                  <TableCell>Rancilio Silvia, Gaggia Classic</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>lever_espresso_machine</code>
                  </TableCell>
                  <TableCell>Lever/Manual Espresso Machine</TableCell>
                  <TableCell>La Pavoni, Flair 58</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>portable_espresso_machine</code>
                  </TableCell>
                  <TableCell>Portable Espresso Machine</TableCell>
                  <TableCell>Outin Nano, Flair Neo</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>automatic_espresso_machine</code>
                  </TableCell>
                  <TableCell>Automatic Espresso Machine</TableCell>
                  <TableCell>Jura, Delonghi</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Accessories (30+ types)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Accessories are categorized into specialized types including:
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Grinder Accessories</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="burr_set" secondary="Replacement burrs" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="dosing_cup" secondary="Single dose cups" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="dosing_tube" secondary="Dosing vials" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="grinder_bellows" secondary="Anti-popcorn bellows" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Espresso Workflow</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="tamper" secondary="Espresso tampers" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="wdt_tool" secondary="Distribution tools" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="tamping_station" secondary="Tamping stations" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="knock_box" secondary="Knock boxes" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Portafilter Equipment</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="bottomless_portafilter" secondary="Naked portafilters" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="precision_basket" secondary="Filter baskets" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="portafilter_funnel" secondary="Dosing funnels" />
                </ListItem>
              </List>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Measurement</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="coffee_scale" secondary="Precision scales" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="thermometer" secondary="Temperature probes" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          View All Types
        </Typography>
        See{' '}
        <code>
          backend/canonical_taxonomy.py
        </code>{' '}
        for the complete list of 40+ canonical types with their attributes and affinity scores.
      </Alert>
    </Stack>
  );

  // Shopify integration section
  const renderShopifyIntegration = () => (
    <Stack spacing={3}>
      <Typography variant="h6">Shopify Integration</Typography>

      <Alert severity="warning">
        Taxonomy data is synced to Shopify in two ways: <strong>metafields</strong> (for collections &
        templates) and <strong>namespaced tags</strong> (for quick filtering).
      </Alert>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Metafields (ai_taxonomy namespace)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" gutterBottom>
            Taxonomy fields are synced as Shopify metafields under the <code>ai_taxonomy</code> namespace:
          </Typography>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metafield Key</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Example Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>canonical_type</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>single_dose_espresso_grinder</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>canonical_type_display_name</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>Single Dose Espresso Grinder</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>equipment_category</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>grinder</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>skill_level_required</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>intermediate</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>brew_focus</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>espresso_primary</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>system_memberships</code>
                  </TableCell>
                  <TableCell>list.single_line_text</TableCell>
                  <TableCell>["df64_family"]</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>grind_range</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>espresso_only</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>maintenance_level</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>medium</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>power_requirement</code>
                  </TableCell>
                  <TableCell>single_line_text</TableCell>
                  <TableCell>230v</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>ideal_for</code>
                  </TableCell>
                  <TableCell>list.single_line_text</TableCell>
                  <TableCell>["Home Barista", "Small Kitchens"]</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>feature_highlights</code>
                  </TableCell>
                  <TableCell>list.single_line_text</TableCell>
                  <TableCell>["Single Dose", "Stepless Adjustment"]</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>compatible_equipment</code>
                  </TableCell>
                  <TableCell>list.single_line_text</TableCell>
                  <TableCell>["58mm portafilters", "E61 group heads"]</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
          <Alert severity="info" sx={{ mt: 2 }}>
            Fields <code>ideal_for</code>, <code>feature_highlights</code>, <code>compatible_equipment</code>,
            and <code>power_requirement</code> are written by the n8n Taxonomy Agent and stored in{' '}
            <code>product_taxonomy_overrides</code>. The nightly batch promotes them into{' '}
            <code>product_taxonomy</code> and they sync here. Auto-derived fields (
            <code>equipment_category</code>, <code>grind_range</code>, etc.) are computed by{' '}
            <code>taxonomy_enhancements.py</code> from <code>canonical_type</code>.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Product Tags (tax: namespace)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" gutterBottom>
            Taxonomy is also synced as namespaced tags for quick filtering:
          </Typography>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tag Namespace</TableCell>
                  <TableCell>Example Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <code>tax:type:</code>
                  </TableCell>
                  <TableCell>tax:type:single_dose_espresso_grinder</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:category:</code>
                  </TableCell>
                  <TableCell>tax:category:grinder</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:brew:</code>
                  </TableCell>
                  <TableCell>tax:brew:espresso, tax:brew:filter</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:skill:</code>
                  </TableCell>
                  <TableCell>tax:skill:intermediate</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:price:</code>
                  </TableCell>
                  <TableCell>tax:price:mid, tax:price:premium</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:role:</code>
                  </TableCell>
                  <TableCell>tax:role:primary, tax:role:secondary</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:form:</code>
                  </TableCell>
                  <TableCell>tax:form:countertop, tax:form:portable</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <code>tax:occasion:</code>
                  </TableCell>
                  <TableCell>tax:occasion:home_barista, tax:occasion:travel</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
          <Alert severity="info" sx={{ mt: 2 }}>
            Manual marketing tags are preserved - only <code>tax:*</code> tags are auto-generated.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Smart Collections</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              The system automatically creates and maintains 15+ Shopify smart collections based on taxonomy:
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2">Equipment Collections</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Espressomaschinen" secondary="All espresso machines" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Elektrische Kaffeemühlen" secondary="All electric grinders" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Handkaffeemühlen" secondary="All hand grinders" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Mahlscheiben Upgrades" secondary="Replacement burr sets" />
                  </ListItem>
                </List>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2">Curated Collections</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Home Barista Starter Set" secondary="Beginner equipment" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Sip & Surf (Travel)" secondary="Portable gear" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Barista Pro Auswahl" secondary="Premium setups" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Outdoor Espresso Set" secondary="Compact portable" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <CodeBlock
              code={`// Example collection rule in Shopify:
// "Elektrische Kaffeemühlen"
Product metafield ai_taxonomy.equipment_category equals "grinder"
AND Product metafield ai_taxonomy.grinder_style equals "electric"`}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Using in Liquid Templates</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" gutterBottom>
            Access taxonomy data in your Shopify theme templates:
          </Typography>
          <CodeBlock
            language="liquid"
            code={`{% assign skill = product.metafields.ai_taxonomy.skill_level_required %}
{% if skill == "beginner" %}
  <span class="badge">Great for beginners!</span>
{% elsif skill == "advanced" %}
  <span class="badge badge-pro">Advanced equipment</span>
{% endif %}

{% assign systems = product.metafields.ai_taxonomy.system_memberships %}
{% for system in systems %}
  <span class="tag">{{ system }}</span>
{% endfor %}`}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  // Recommendations section
  const renderRecommendations = () => (
    <Stack spacing={3}>
      <Typography variant="h6">Product Recommendations & Relations</Typography>

      <Alert severity="info">
        The relations system uses taxonomy to generate intelligent product recommendations by analyzing
        compatibility, complementary needs, and historical purchase data.
      </Alert>

      <FlowDiagram
        title="How Recommendations Work"
        steps={[
          {
            icon: <Typography fontWeight={600}>1</Typography>,
            title: 'Taxonomy Matching',
            description: 'Compare canonical types, brew focus, skill levels',
          },
          {
            icon: <Typography fontWeight={600}>2</Typography>,
            title: 'Affinity Scoring',
            description: 'Apply type affinity matrix (e.g., grinder + burrs = 0.85)',
          },
          {
            icon: <Typography fontWeight={600}>3</Typography>,
            title: 'Co-purchase Data',
            description: 'Analyze what customers buy together',
          },
          {
            icon: <Typography fontWeight={600}>4</Typography>,
            title: 'Relation Priors',
            description: 'Apply manual bias adjustments from admin',
          },
          {
            icon: <Typography fontWeight={600}>5</Typography>,
            title: 'Final Score',
            description: 'Weighted combination → match_score & upsell_score',
          },
        ]}
      />

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Relation Types</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Relation Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Examples</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="complement" size="small" color="primary" />
                  </TableCell>
                  <TableCell>Core add-on or upgrade</TableCell>
                  <TableCell>Grinder → Burr Set, Espresso Machine → Tamper</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="addon" size="small" />
                  </TableCell>
                  <TableCell>Small accessory or consumable</TableCell>
                  <TableCell>Grinder → Dosing Cup, Machine → Cleaning Tablets</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="competitor" size="small" />
                  </TableCell>
                  <TableCell>Alternative with same core function</TableCell>
                  <TableCell>
                    DF64 Gen 2 ↔ Timemore Sculptor, Flair 58 ↔ Outin Nano
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Type Affinity Matrix</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" gutterBottom>
            The affinity matrix defines base compatibility scores between canonical types:
          </Typography>
          <CodeBlock
            code={`// Examples from TYPE_AFFINITY_MATRIX
single_dose_espresso_grinder → burr_set: 0.85 (high compatibility)
single_dose_espresso_grinder → tamper: 0.75
single_dose_espresso_grinder → coffee_scale: 0.80
hand_grinder_espresso → portable_espresso_machine: 0.90 (travel combo)
bottomless_portafilter → wdt_tool: 0.85 (workflow synergy)`}
          />
          <Alert severity="success" sx={{ mt: 2 }}>
            These base scores are combined with co-purchase data and relation priors to generate final
            recommendations.
          </Alert>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">System Memberships & Compatibility</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              Products can belong to "systems" (vendor ecosystems) for precise compatibility matching:
            </Typography>
            <TableViewport>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>System</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Compatible Equipment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <code>df64_family</code>
                    </TableCell>
                    <TableCell>DF Coffee</TableCell>
                    <TableCell>DF54, DF64, DF83 grinders + compatible burrs</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <code>timemore_system</code>
                    </TableCell>
                    <TableCell>Timemore</TableCell>
                    <TableCell>Chestnut, Sculptor + accessories</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <code>flair_system</code>
                    </TableCell>
                    <TableCell>Flair</TableCell>
                    <TableCell>Flair 58, Neo, Pro + portafilters</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <code>acaia_system</code>
                    </TableCell>
                    <TableCell>Acaia</TableCell>
                    <TableCell>Lunar, Pearl, Orbit scales</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableViewport>
            <Typography variant="body2">
              Products in the same system receive a +20% compatibility boost in recommendations.
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Spare Part Compatibility</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              For physical parts that only fit specific models, use explicit compatibility overrides:
            </Typography>
            <CodeBlock
              code={`// In product_taxonomy_overrides:
canonical_type: "spare_part"
compatible_product_ids: ["15254606643577", "15254606644577"]  // DF64 Gen 2 only

// Results in:
- +35% match_score boost for these specific products
- Works bidirectionally (spare part ↔ product)
- Ensures recommendations are physically compatible`}
            />
            <Typography variant="caption" color="text.secondary">
              Use spare part compatibility for burrs, portafilters, pressure gauges, and other
              model-specific parts.
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  // Pipeline execution section
  const renderPipelineExecution = () => (
    <Stack spacing={3}>
      <Typography variant="h6">Running the Taxonomy Pipeline</Typography>

      <Alert severity="warning">
        The taxonomy pipeline should be run after product syncs, bulk overrides, or when adding new canonical
        types.
      </Alert>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Full Pipeline via Admin API (Recommended)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">Trigger all stages from the Admin panel:</Typography>
            <CodeBlock
              code={`POST /api/admin/taxonomy/pipeline
X-Admin-Key: <your-admin-key>

Runs:
1. taxonomy_batch.py (derive taxonomy)
2. sync_taxonomy_metafields.py (push to Shopify)
3. create_dynamic_collections.py (create collections)
4. relations_batch.py (generate recommendations)`}
            />
            <Button variant="contained" size="small" sx={{ alignSelf: 'flex-start' }}>
              Run pipeline now (Admin &gt; Sync Center)
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Manual Stage-by-Stage Execution</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="subtitle2">Stage 1: Generate Taxonomy</Typography>
            <CodeBlock
              code={`# Process all products
python3 -m backend.taxonomy_batch --verbose

# Test with limited products
python3 -m backend.taxonomy_batch --limit 20 --verbose

# Process single product
python3 -m backend.taxonomy_batch --product-id 15254606643577 --verbose

# Dry run (preview without saving)
python3 -m backend.taxonomy_batch --dry-run --limit 10`}
            />

            <Divider />

            <Typography variant="subtitle2">Stage 2: Create Shopify Metafield Definitions</Typography>
            <CodeBlock
              code={`# Create metafield definitions (run once)
python3 -m backend.sync_taxonomy_metafields --create-definitions --owner-type PRODUCT
python3 -m backend.sync_taxonomy_metafields --create-definitions --owner-type VARIANT`}
            />

            <Divider />

            <Typography variant="subtitle2">Stage 3: Sync Metafields to Products</Typography>
            <CodeBlock
              code={`# Sync all products
python3 -m backend.sync_taxonomy_metafields --owner-type PRODUCT

# Sync specific product
python3 -m backend.sync_taxonomy_metafields --product-id 15254606643577 --owner-type PRODUCT

# Sync variants
python3 -m backend.sync_taxonomy_metafields --owner-type VARIANT`}
            />

            <Divider />

            <Typography variant="subtitle2">Stage 4: Generate Product Tags</Typography>
            <CodeBlock
              code={`# Dry run (preview tags)
python3 -m backend.sync_product_tags --all --dry-run --verbose

# Sync tags to all products
python3 -m backend.sync_product_tags --all --verbose`}
            />

            <Divider />

            <Typography variant="subtitle2">Stage 5: Create Dynamic Collections</Typography>
            <CodeBlock
              code={`# Create collections in Shopify
python3 -m backend.create_dynamic_collections --create --debug`}
            />

            <Divider />

            <Typography variant="subtitle2">Stage 6: Generate Product Relations</Typography>
            <CodeBlock
              code={`# Full rebuild (after taxonomy changes)
python3 -m backend.relations_batch

# Incremental update (only products with new orders)
python3 -m backend.relations_batch --incremental`}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">When to Run Each Stage</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scenario</TableCell>
                  <TableCell>Stages to Run</TableCell>
                  <TableCell>Frequency</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>New products added to catalog</TableCell>
                  <TableCell>1, 3, 4, 6 (taxonomy → metafields → tags → relations)</TableCell>
                  <TableCell>Nightly</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Bulk taxonomy overrides applied</TableCell>
                  <TableCell>1, 3, 4, 6 (full pipeline)</TableCell>
                  <TableCell>As needed</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>New canonical type added</TableCell>
                  <TableCell>1, 2, 3, 4, 5, 6 (complete pipeline)</TableCell>
                  <TableCell>Rarely</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Collection rules updated</TableCell>
                  <TableCell>5 only (collections)</TableCell>
                  <TableCell>As needed</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>New orders received</TableCell>
                  <TableCell>6 --incremental (relations only)</TableCell>
                  <TableCell>Nightly</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableViewport>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  // Troubleshooting section
  const renderTroubleshooting = () => (
    <Stack spacing={3}>
      <Typography variant="h6">Troubleshooting & Common Issues</Typography>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Product has wrong canonical_type</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600}>
              Diagnosis:
            </Typography>
            <CodeBlock
              code={`# Check if there's an override
SELECT * FROM product_taxonomy_overrides WHERE product_id = '15254606643577';

# Check inferred value
python3 -m backend.taxonomy_batch --product-id 15254606643577 --dry-run --verbose`}
            />
            <Typography variant="body2" fontWeight={600}>
              Solution:
            </Typography>
            <Typography variant="body2">
              1. Navigate to Product Detail page in admin UI
              <br />
              2. Click "Edit Taxonomy Override"
              <br />
              3. Set correct <code>canonical_type</code>
              <br />
              4. Save and re-run taxonomy batch
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Metafields not appearing in Shopify</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600}>
              Steps to fix:
            </Typography>
            <Typography variant="body2">
              1. Verify metafield definitions exist in Shopify (Settings → Custom Data)
              <br />
              2. Re-create definitions if missing:
            </Typography>
            <CodeBlock
              code={`python3 -m backend.sync_taxonomy_metafields --create-definitions --owner-type PRODUCT`}
            />
            <Typography variant="body2">
              3. Check taxonomy exists in database:
            </Typography>
            <CodeBlock
              code={`SELECT * FROM product_taxonomy WHERE product_id = '15254606643577';`}
            />
            <Typography variant="body2">4. Re-sync metafields:</Typography>
            <CodeBlock
              code={`python3 -m backend.sync_taxonomy_metafields --product-id 15254606643577 --owner-type PRODUCT --verbose`}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Tags not appearing on products</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600}>
              Common causes:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Tag sync not run"
                  secondary="Run: python3 -m backend.sync_product_tags --all --verbose"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Shopify API rate limits"
                  secondary="Script may have been rate-limited; check logs for errors"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Product not in database"
                  secondary="Ensure product_id matches between database and Shopify"
                />
              </ListItem>
            </List>
            <CodeBlock
              code={`# Re-run tag sync with verbose logging
python3 -m backend.sync_product_tags --all --verbose 2>&1 | tee tag_sync.log`}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Collections not updating automatically</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              Shopify smart collections should auto-update when metafields change. If not:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Check collection conditions"
                  secondary="Verify the metafield key and value exactly match (case-sensitive)"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Manually trigger collection update"
                  secondary="In Shopify Admin → Collections → [Collection Name] → Click 'View all conditions'"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Re-create collection if broken"
                  secondary="Run: python3 -m backend.create_dynamic_collections --create --debug"
                />
              </ListItem>
            </List>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Recommendations seem wrong or missing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" fontWeight={600}>
              Diagnosis:
            </Typography>
            <CodeBlock
              code={`# Check if relations exist
SELECT COUNT(*) FROM product_relations WHERE anchor_product_id = '15254606643577';

# View top recommendations
SELECT * FROM product_relations
WHERE anchor_product_id = '15254606643577'
ORDER BY match_score DESC
LIMIT 10;`}
            />
            <Typography variant="body2" fontWeight={600}>
              Solutions:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Rebuild relations"
                  secondary="POST /api/admin/relations/recompute with incremental: false"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Check taxonomy data"
                  secondary="Ensure both anchor and candidate products have canonical_type assigned"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Review affinity matrix"
                  secondary="Check TYPE_AFFINITY_MATRIX in canonical_taxonomy.py for expected pairings"
                />
              </ListItem>
            </List>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  const tabs = [
    { label: 'Overview', content: renderOverview() },
    { label: 'Architecture', content: renderArchitecture() },
    { label: 'Canonical Types', content: renderCanonicalTypes() },
    { label: 'Shopify Integration', content: renderShopifyIntegration() },
    { label: 'Recommendations', content: renderRecommendations() },
    { label: 'Pipeline', content: renderPipelineExecution() },
    { label: 'Troubleshooting', content: renderTroubleshooting() },
  ];

  return (
    <PageContainer>
      <ViewHeader title="Product Taxonomy System" subtitle="Complete guide to the taxonomy classification system" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ py: 2 }}>{tabs[activeTab].content}</Box>
    </PageContainer>
  );
}
